<script>
  import { CheckCircle2, Download, FolderOpen } from 'lucide-svelte';
  import { onDestroy } from 'svelte';
  import { normalizeUrl } from '$lib/utils';

  export let url = '';
  export let name = '';
  export let size = '';
  export let mime = '';
  export let onOpen;

  let remoteSizeLabel = '';
  let activeSizeUrl = '';
  let sizeAbortController = null;
  let isDownloading = false;

  $: fileUrl = normalizeUrl(url);
  $: fileName = getFileName(name || fileUrl);
  $: explicitSizeLabel = formatSize(size);
  $: sizeLabel = explicitSizeLabel || remoteSizeLabel;
  $: fileTypeLabel = String(mime || '')
    .toLowerCase()
    .includes('pdf')
    ? 'PDF'
    : 'PDF';
  $: metaText = [fileTypeLabel, sizeLabel].filter(Boolean).join(' · ');
  $: canOpen = Boolean(fileUrl);

  $: if (canOpen && !explicitSizeLabel && fileUrl !== activeSizeUrl) {
    void loadRemoteSize(fileUrl);
  }

  function getFileName(value) {
    const fallback = 'document.pdf';
    const raw = String(value || '').trim();
    if (!raw) return fallback;

    try {
      const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const hintedName = parsed.searchParams.get('name');
      const pathnameName = parsed.pathname.split('/').filter(Boolean).pop();
      return safeDecode(hintedName || pathnameName || fallback);
    } catch {
      return safeDecode(raw.split(/[?#]/, 1)[0].split('/').filter(Boolean).pop() || fallback);
    }
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function formatSize(value) {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;

    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let sizeValue = bytes;
    let unitIndex = 0;

    while (sizeValue >= 1024 && unitIndex < units.length - 1) {
      sizeValue /= 1024;
      unitIndex += 1;
    }

    return `${sizeValue.toFixed(sizeValue >= 10 || unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  }

  function isSameOrigin(targetUrl) {
    if (typeof window === 'undefined') return false;

    try {
      return new URL(targetUrl, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  function getCredentialMode(targetUrl) {
    return isSameOrigin(targetUrl) ? 'include' : 'omit';
  }

  function triggerDownload(href, downloadName) {
    const link = document.createElement('a');
    link.href = href;
    link.download = downloadName || 'document.pdf';
    link.rel = 'noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function downloadPdf() {
    if (!canOpen || isDownloading) return;

    isDownloading = true;

    try {
      const response = await fetch(fileUrl, { credentials: getCredentialMode(fileUrl) });
      if (!response.ok) throw new Error('download_failed');

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, fileName);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1200);
    } catch {
      // Fallback keeps the action as a download attempt instead of opening the PDF card preview.
      triggerDownload(fileUrl, fileName);
    } finally {
      isDownloading = false;
    }
  }

  async function loadRemoteSize(targetUrl) {
    activeSizeUrl = targetUrl;
    remoteSizeLabel = '';
    sizeAbortController?.abort();
    sizeAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    try {
      const response = await fetch(targetUrl, {
        method: 'HEAD',
        credentials: isSameOrigin(targetUrl) ? 'include' : 'omit',
        signal: sizeAbortController?.signal
      });

      if (!response.ok || targetUrl !== activeSizeUrl) return;

      const contentLength = response.headers.get('content-length');
      remoteSizeLabel = formatSize(Number(contentLength));
    } catch {
      // Some file endpoints do not allow HEAD/CORS. The preview still works without size.
    }
  }

  function openDetails() {
    if (!canOpen) return;
    onOpen?.(fileUrl);
  }

  onDestroy(() => {
    sizeAbortController?.abort();
  });
</script>

<section class="pdf-card" aria-label={`PDF: ${fileName}`}>
  <button
    class="preview-button"
    type="button"
    disabled={!canOpen}
    title="Đọc chi tiết PDF"
    aria-label={`Đọc chi tiết ${fileName}`}
    on:click|stopPropagation={openDetails}>
    <div class="preview-frame" aria-hidden="true">
      <div class="doc-stack">
        <span class="doc-page doc-page--back"></span>
        <span class="doc-page doc-page--middle"></span>
        <span class="doc-page doc-page--front">
          <span class="doc-line short"></span>
          <span class="doc-line"></span>
          <span class="doc-box"></span>
        </span>
      </div>
    </div>
  </button>

  <div class="file-strip">
    <div class="pdf-badge" aria-hidden="true">PDF</div>

    <button
      class="file-info"
      type="button"
      disabled={!canOpen}
      title="Đọc chi tiết"
      aria-label={`Đọc chi tiết ${fileName}`}
      on:click|stopPropagation={openDetails}>
      <strong>{fileName}</strong>
      <span>
        {metaText}
        <span class="ready"><CheckCircle2 size={13} strokeWidth={2.2} /> Đã có trên máy</span>
      </span>
    </button>

    <div class="quick-actions">
      <button
        type="button"
        disabled={!canOpen}
        title="Đọc chi tiết"
        aria-label={`Đọc chi tiết ${fileName}`}
        on:click|stopPropagation={openDetails}>
        <FolderOpen size={17} strokeWidth={2} />
      </button>

      {#if canOpen}
        <button
          type="button"
          disabled={!canOpen || isDownloading}
          title={isDownloading ? 'Đang tải PDF' : 'Tải PDF'}
          aria-label={`Tải ${fileName}`}
          on:click|preventDefault|stopPropagation={downloadPdf}>
          <Download size={17} strokeWidth={2} />
        </button>
      {/if}
    </div>
  </div>
</section>

<style>
  .pdf-card {
    width: min(100%, 24.2rem);
    color: #0f172a;
  }

  .preview-button,
  .file-info,
  .quick-actions button {
    appearance: none;
    -webkit-tap-highlight-color: transparent;
  }

  .preview-button {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    border-radius: 0.42rem;
    background: transparent;
    cursor: pointer;
  }

  .preview-button:disabled,
  .file-info:disabled,
  .quick-actions button:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  .preview-frame {
    display: grid;
    place-items: center;
    height: clamp(10rem, 26vw, 12.2rem);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 0.42rem;
    background: #f8fafc;
  }

  .doc-stack {
    position: relative;
    width: 4.2rem;
    height: 4.9rem;
  }

  .doc-page {
    position: absolute;
    display: block;
    width: 3.1rem;
    height: 4rem;
    border: 1px solid #d5dadd;
    border-radius: 0.22rem;
    background: linear-gradient(180deg, #f4f5f5, #e8ebec);
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.06);
  }

  .doc-page--back {
    top: 0.2rem;
    left: 0.88rem;
    opacity: 0.58;
  }

  .doc-page--middle {
    top: 0.5rem;
    left: 0.56rem;
    opacity: 0.72;
  }

  .doc-page--front {
    top: 0.82rem;
    left: 0.22rem;
    display: grid;
    align-content: start;
    gap: 0.34rem;
    padding: 0.75rem 0.5rem 0.48rem;
    background: #eef0f1;
  }

  .doc-page--front::before {
    content: '';
    position: absolute;
    top: -1px;
    right: -1px;
    border-top: 0.72rem solid #dfe3e5;
    border-left: 0.72rem solid transparent;
  }

  .doc-line {
    display: block;
    height: 0.2rem;
    border-radius: 999px;
    background: #cfd4d7;
  }

  .doc-line.short {
    width: 1.58rem;
  }

  .doc-box {
    display: block;
    width: 2rem;
    height: 1.28rem;
    margin-top: 0.32rem;
    border: 1px solid #cbd1d4;
    border-radius: 0.14rem;
    background: #dfe3e5;
  }

  .file-strip {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.65rem;
    margin-top: 0.9rem;
  }

  .pdf-badge {
    position: relative;
    display: grid;
    place-items: end center;
    width: 2.45rem;
    height: 3rem;
    padding-bottom: 0.48rem;
    border-radius: 0.32rem;
    color: #fff;
    background: #ff5b55;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.08);
  }

  .pdf-badge::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    border-top: 0.72rem solid rgba(255, 255, 255, 0.74);
    border-left: 0.72rem solid rgba(185, 28, 28, 0.34);
  }

  .file-info {
    display: grid;
    min-width: 0;
    gap: 0.26rem;
    padding: 0;
    border: 0;
    text-align: left;
    background: transparent;
    cursor: pointer;
  }

  .file-info strong {
    overflow: hidden;
    color: #14233b;
    font-size: 0.9rem;
    font-weight: 750;
    line-height: 1.22;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-info > span {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.42rem;
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.25;
  }

  .ready {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    color: #16834a;
  }

  .quick-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .quick-actions button {
    display: grid;
    place-items: center;
    width: 1.82rem;
    height: 1.82rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.42rem;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.92);
    text-decoration: none;
    cursor: pointer;
  }

  .quick-actions button:hover,
  .preview-button:hover .preview-frame {
    border-color: #94a3b8;
  }

  @media (max-width: 520px) {
    .pdf-card {
      width: min(100%, calc(100vw - 4.6rem));
    }

    .preview-frame {
      height: 10.4rem;
    }

    .file-strip {
      gap: 0.52rem;
    }

    .file-info strong {
      font-size: 0.84rem;
    }

    .file-info > span {
      font-size: 0.76rem;
    }
  }
</style>
